import BttfAxios from "@features/utils/axios";
import {
  IAddCryptoWallet,
  IAddBankAccount,
} from "@funds/components/WithdrawForm/IWithdrawForm";
import { IWithdrawData } from "@funds/components/WithdrawVerification/IWithdrawVerification";
import { IPurchaseTokensData } from "@funds/components/PurchaseForm/IPurchaseForm";
import {
  TInvestorBankAccountDetails,
  TBankDetails,
} from "@funds/components/BankForm/IBankForm";
import { ITradeTokensData } from "@appInterfaces/fundsService.interfaces";
import { ICryptoTradeData } from "@funds/components/ExchangeForm/IExchangeForm";
import { AxiosResponse } from "axios";

export type StakingResponse = {
  amount: string;
  created_at: string;
  id: number;
  status: string;
  total_usd: string;
  timelock_duration: number;
};

class FundsServiceClass {
  public getWallets(investorId: number, currency: string = "") {
    let params = "";

    if (currency) {
      params = BttfAxios.paramBuilder({ currency });
    }

    return BttfAxios.get(
      `/api/investors/${investorId}/wallets/${params}`,
      (resp) => resp
    );
  }

  public getPESWallets(investorId: number, currency: string = "") {
    let params = "";

    if (currency) {
      params = BttfAxios.paramBuilder({ currency });
    }

    return BttfAxios.get(
      `/api/investors/${investorId}/public_equity_shares/wallets/${params}`,
      (resp) => resp
    );
  }

  public getBankAccounts(investorId: number) {
    return BttfAxios.get(
      `/api/funds/investors/${investorId}/bank_accounts`,
      (resp) => resp
    );
  }

  public getStakingEth(investorId: number) {
    return BttfAxios.get(
      `  /api/investors/${investorId}/staking/staked_eth`,
      (resp) => resp
    );
  }

  public getBankAccountFromPath(path: string) {
    return BttfAxios.get(path, (resp) => resp);
  }

  public getPrivateWallets(investorId: number) {
    return BttfAxios.get(
      `/api/investors/${investorId}/private_wallets`,
      (resp) => resp
    );
  }

  public getWithdrawsHistory(
    investorId: number,
    currency?: string,
    page?: number
  ) {
    const params = currency ? BttfAxios.paramBuilder({ currency, page }) : "";
    return BttfAxios.get(
      `/api/investors/${investorId}/withdraws/${params}`,
      (resp) => resp
    );
  }

  public getInvestorIdentityData(investorId: number): Promise<{
    data: TInvestorBankAccountDetails;
  }> {
    return BttfAxios.get(
      `/api/funds/investors/${investorId}/bank_account_holder`,
      (resp) => resp
    );
  }

  public getBankDetails(swiftBic: string): Promise<{ data: TBankDetails }> {
    return BttfAxios.get(
      `/api/funds/banks/${swiftBic}`,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public oldGetWithdrawsHistoryDetails(withdrawId: number) {
    return BttfAxios.get(`/api/withdraws/${withdrawId}`, (resp) => resp);
  }

  public getWithdrawsHistoryDetails(investorId: number, withdrawId: number) {
    return BttfAxios.get(
      `/api/investors/${investorId}/withdraws/${withdrawId}`,
      (resp) => resp
    );
  }

  public getWithdrawFromPath(path: string) {
    return BttfAxios.get(path, (resp) => resp);
  }

  public getDepositsHistory(
    investorId: number,
    currency?: string,
    page?: number
  ) {
    const params = currency ? BttfAxios.paramBuilder({ currency, page }) : "";
    return BttfAxios.get(`/api/deposits/${params}`, (resp) => resp);
  }

  public getPurchasesHistory(currency?: string, page?: number) {
    const params = currency ? BttfAxios.paramBuilder({ currency, page }) : "";
    return BttfAxios.get(`/api/token_purchases/${params}`, (resp) => resp);
  }

  public getTradingHistory(currency?: string, page?: number) {
    const params = currency ? BttfAxios.paramBuilder({ currency, page }) : "";
    return BttfAxios.get(
      `/api/funds/crypto_trading/current_investor/crypto_trades/${params}`,
      (resp) => resp
    );
  }

  public getPurchasesHistoryDetails(transactionId: number) {
    return BttfAxios.get(
      `/api/token_purchases/${transactionId}`,
      (resp) => resp
    );
  }

  public getCryptoTradingHistoryDetails(tradeId: number) {
    return BttfAxios.get(
      `/api/funds/crypto_trading/current_investor/crypto_trades/${tradeId}`,
      (resp) => resp
    );
  }

  public cancelWithdraw(investorId: number, transactionId: number) {
    return BttfAxios.post(
      `/api/investors/${investorId}/withdraws/${transactionId}/cancel`,
      undefined,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public addBankAccount(investorId: number, data: IAddBankAccount) {
    const formData = new FormData();
    formData.append("bic_swift", data.bic_swift);
    formData.append("account_number", data.account_number);
    formData.append("currency", data.currency);
    data?.bank_statement &&
      formData.append("bank_statement", data.bank_statement);
    return BttfAxios.post(
      `/api/funds/investors/${investorId}/bank_accounts`,
      formData,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public addCryptoWallet(investorId: number, data: IAddCryptoWallet) {
    return BttfAxios.post(
      `/api/investors/${investorId}/private_wallets`,
      data,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public makeWithdrawal(investorId: number, data: IWithdrawData) {
    return BttfAxios.post(
      `/api/investors/${investorId}/withdraws`,
      data,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getTransactionsHistory = (
    investorId: number,
    page: number | undefined,
    currency: string | undefined,
    date_from: string | undefined,
    date_to: string | undefined
  ) => {
    let params = "";

    if (page || date_from || date_to || currency) {
      params = BttfAxios.paramBuilder({ page, date_from, date_to, currency });
    }

    return BttfAxios.get(
      process.env.ENABLE_BINANCE_TRADING === "true"
        ? `/api/funds/investors/${investorId}/transactions/${params}`
        : `/api/investors/${investorId}/transaction_payments/${params}`,
      (resp) => resp
    );
  };

  // TODO: Remove with ENABLE_BINANCE_TRADING
  public getTransactionsHistoryDetails = (transactionId: number) => {
    return BttfAxios.get(`/api/transactions/${transactionId}`, (resp) => resp);
  };

  public updateCryptoWallet(
    investorId: number,
    data: IAddCryptoWallet,
    id: number
  ) {
    return BttfAxios.patch(
      `/api/investors/${investorId}/private_wallets/${id}`,
      data,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public deleteCryptoWallet(investorId: number, walletId: number) {
    return BttfAxios.delete(
      `/api/investors/${investorId}/private_wallets/${walletId}`,
      undefined,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getDepositWallets(currency: string, network?: string) {
    let url = `/api/funds/current_investor/deposit_addresses/${currency}`;
    if (network) {
      url = `${url}?network_type=${network}`;
    }

    return BttfAxios.get(url, (resp) => resp);
  }

  public getPurchaseExchangeRates(currency: string) {
    return BttfAxios.get(
      `/api/exchange_rates?base_currency=${currency}`,
      (resp) => resp
    );
  }

  public getTradingExchangeRates(
    incomeCurrency: string,
    outcomeCurrency: string
  ) {
    return BttfAxios.get(
      `/api/funds/crypto_trading/exchange_rates?income_currency=${incomeCurrency}&outcome_currency=${outcomeCurrency}`,
      (resp) => resp
    );
  }

  public getTradingSettings() {
    return BttfAxios.get(
      "/api/funds/crypto_trading/current_investor/settings",
      (resp) => resp
    );
  }

  public purchaseToken(data: IPurchaseTokensData) {
    const url =
      process.env.ENABLE_BINANCE_TRADING === "true"
        ? "/api/funds/crypto_trading/current_investor/internal_crypto_trades"
        : "/api/token_purchases";

    return BttfAxios.post(
      url,
      data,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public tradeTokens(data: ICryptoTradeData) {
    return BttfAxios.post(
      `/api/funds/crypto_trading/current_investor/crypto_trades`,
      data,
      (res: ITradeTokensData) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getDepositBankAccounts(currency: string) {
    return BttfAxios.get(
      `/api/deposits/bank_accounts/${currency}`,
      (resp) => {
        return Promise.resolve(resp);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getDepositBankAccount() {
    return BttfAxios.get(
      `/api/funds/current_investor/deposit_bank_accounts`,
      (resp) => {
        return Promise.resolve(resp);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public oldGetDepositBankAccount() {
    return BttfAxios.get(
      `/api/deposits/bank_account`,
      (resp) => {
        return Promise.resolve(resp);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public rejectBankAccount(investorId: number, accountId: number) {
    return BttfAxios.post(
      `/api/funds/investors/${investorId}/bank_accounts/${accountId}/reject`,
      undefined,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getWithdrawFees() {
    return BttfAxios.get(
      "/api/funds/withdraws/settings",
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public updateAccessingFunds() {
    return BttfAxios.patch(
      "/api/funds/investors/accessing_funds",
      undefined,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getStaking(): Promise<AxiosResponse<Array<StakingResponse>>> {
    return BttfAxios.get(
      "/api/staking/stakes",
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getRewards(investorId: number) {
    return BttfAxios.get(
      `/api/investors/${investorId}/staking/rewards`,
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public postStaking(
    amount: number,
    tAndC: boolean,
    timelockFee: number | null,
    timelockDuration: number | null,
    percentageAmount?: string
  ) {
    return BttfAxios.post(
      "/api/staking/stakes",
      {
        amount,
        terms_and_conditions_accepted: tAndC,
        timelockFee,
        timelockDuration,
        percentageAmount,
      },
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public unstaking(amount: string) {
    return BttfAxios.put(
      "/api/staking/stakes",
      {
        amount,
      },
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public stakedEthApr() {
    return BttfAxios.get(
      "/api/staking/network_overviews",
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getAutoStaking() {
    return BttfAxios.get(
      "/api/staking/settings",
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public getTimelockStakingSettings() {
    return BttfAxios.get(
      "/api/staking/timelock_settings",
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public putAutoStaking(autoRestaking: boolean) {
    return BttfAxios.put(
      "/api/staking/settings",
      {
        auto_restaking: autoRestaking,
      },
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }

  public addTimelocks(
    stakingTransactionIds: number[],
    timelockDuration: string | null
  ) {
    return BttfAxios.post(
      "/api/staking/add_timelocks",
      {
        staking_transaction_ids: stakingTransactionIds,
        timelock_duration: timelockDuration,
      },
      (res) => {
        return Promise.resolve(res);
      },
      (err) => {
        return Promise.reject(err);
      }
    );
  }
}

const FundsService = new FundsServiceClass();

export default FundsService;
